import {MultistoreMenuCategory, MultistoreMenuItem} from "./types";
import {getElementTranslation, getElementTranslations} from "../../../services/languageUtils";

export const getParsedMultistoreMenuCategories = (
    menu: Record<string, any>,
    categories: Record<string, any>[],
    locale: string,
    items: Record<string, any>[],
    itemGroups: Record<string, any>[]
): MultistoreMenuCategory[] => {
    const menuCategories = categories.filter(category => category.status === "ENABLED" && menu.categories.some((menuCategory: Record<string, any>) => menuCategory.category_id === category.id));

    return menuCategories.map((category: Record<string, any>): MultistoreMenuCategory => {
        const categoryItems = getParsedCategoryItems(category, items, itemGroups, locale);
        const image = categoryItems.find(item => item.image_small)?.image_small;

        const categoryFromMenu = menu.categories.find((menuCategory: Record<string, any>) => menuCategory.category_id === category.id);
        return {
            name: getElementTranslation(locale, category, ["name"])?.name || category.name,
            description: getElementTranslation(locale, category, ["description"])?.description || category.description,
            id: category.id,
            items: categoryItems,
            image,
            position: categoryFromMenu.position
        }
    }).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
};

const getParsedCategoryItems = (
    category: Record<string, any>,
    items: Record<string, any>[],
    itemGroups: Record<string, any>[],
    locale: string
): MultistoreMenuItem[] => {
    const enabledItems = items.filter(item => item.status === "ENABLED");

    return category.entities.map((entity: Record<string, any>): MultistoreMenuItem | undefined => {
        const item = enabledItems.find(item => item.id === entity.entity_id);

        if (!item) return undefined;

        const itemGroup = itemGroups.find(itemGroup => itemGroup.items.some((itemItemGroup: Record<string, any>) => itemItemGroup.item_id === item.id));

        if (!itemGroup) return undefined;

        const itemsInItemGroup = itemGroup.items.map((itemGroupItem: Record<string, any>) => items.find(item => item.id === itemGroupItem.item_id)).filter(Boolean);
        const categoryItemsInItemGroup = itemsInItemGroup.filter((itemGroupItem: Record<string, any>) => category.entities.some((categoryEntity: Record<string, any>) => categoryEntity.entity_id === itemGroupItem.id));
        const cheapestItem = categoryItemsInItemGroup.sort((a: Record<string, any>, b: Record<string, any>) => a.price.amount - b.price.amount)[0];
        let description = getElementTranslation(locale, cheapestItem, ["description"])?.description || cheapestItem.description;

        if (!description) {
            const cheapestItemModifiers = cheapestItem.quantity_info_overrides
                .filter((quantityInfo: Record<string, any>) => quantityInfo.quantity_info.default_quantity > 0 && quantityInfo.context_type === "ITEM")
                .flatMap((quantityInfo: Record<string, any>) => {
                        const item = items.find(i => i.id === Number(quantityInfo.context_id));

                        if (!item) return undefined;

                        return {
                            name: getElementTranslation(locale, item, ["name"])?.name || item?.name,
                            quantity: quantityInfo.default_quantity,
                        }
                    }
                ).filter(Boolean);
            description = cheapestItemModifiers.map((modifier: Record<string, any>) => {
                const modifierName = getElementTranslation(locale, modifier, ["name"])?.name || modifier.name;

                if (modifier.quantity > 1) return `x${modifier.quantity} ${modifierName}`;
                return modifierName;

            }).join(", ");
        }

        if (!cheapestItem) return undefined;

        const cheapestItemWithPosition = category.entities.find((entity: Record<string, any>) => entity.entity_id === cheapestItem.id);

        return {
            name: getElementTranslation(locale, itemGroup, ["name"])?.name || itemGroup.name,
            id: cheapestItem.id,
            image_large: cheapestItem.image_large,
            image_small: cheapestItem.image_small,
            image_medium: cheapestItem.image_medium,
            price: cheapestItem.price,
            description,
            position: cheapestItemWithPosition.position
        }
    }).filter(Boolean).filter((item: MultistoreMenuItem, index: number, self: MultistoreMenuItem[]) => self.findIndex(i => i.id === item.id) === index)
        .sort((a: MultistoreMenuItem, b: MultistoreMenuItem) => a.position - b.position || a.name.localeCompare(b.name));
}

export const getParsedMultistoreMenus = (menus: Record<string, any>[], locale: string | undefined) => {
    return menus.map(menu => {
        return {
            ...menu,
            name: getElementTranslations(locale, menus, ["name"])?.name || menu.name
        }
    });
};